import { Children, type ReactNode, isValidElement, cloneElement } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { EntityRef } from "./EntityRef";

/** Centralized markdown renderer for any user-visible markdown source
 *  (synthesized brief bodies, Recall replies, recommendation reasons,
 *  changelog entries, etc.). Replaces the half-dozen ad-hoc
 *  `whitespace-pre-line` paragraphs and the homegrown MarkdownLite
 *  helper that previously rendered `## Heading` as literal text.
 *
 *  Typography decisions are made HERE, once, so every surface picks
 *  up the same heading scale, list spacing, code chip styling, and
 *  link colour.
 *
 *  Citation chips: pass `citations` to render `[N]` patterns inside
 *  text as orange brand chips (used by the Recall conversation reply
 *  bubble so the LLM's `[1] [3]` style references read as tappable
 *  pills instead of plain text). The walker runs after react-markdown
 *  has done its inline parsing, so it composes correctly with bold,
 *  italic, links, and code spans.
 *
 *  Theme respect: every text colour uses the dark/light Tailwind
 *  pair so the rendered markdown stays readable on both canvas
 *  variants. Brand orange (#f97316) is the link + blockquote accent
 *  so links read as Memso, not generic. */
const CITATION_PATTERN = /\[(\d+)\]/g;
const ENTITY_REF_PATTERN = /\[\[([^\]]+)\]\]/g;

function CitationChip({ n }: { n: string }) {
  return (
    <span className="inline-flex items-center justify-center text-[10px] font-bold w-5 h-5 mx-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-200 align-baseline">
      {n}
    </span>
  );
}

/** Recursively walk React children, replacing `[[Name]]` substrings
 *  inside string nodes with <EntityRef> chips. Composes with bold,
 *  italic, links the same way the citation walker does. */
function interleaveEntityRefs(children: ReactNode): ReactNode {
  const out: ReactNode[] = [];
  let counter = 0;
  Children.forEach(children, (child) => {
    if (typeof child === "string") {
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      ENTITY_REF_PATTERN.lastIndex = 0;
      while ((match = ENTITY_REF_PATTERN.exec(child)) !== null) {
        if (match.index > lastIndex) out.push(child.slice(lastIndex, match.index));
        out.push(<EntityRef key={`ent-${counter++}`} name={match[1].trim()} />);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < child.length) out.push(child.slice(lastIndex));
      return;
    }
    if (isValidElement(child)) {
      const props = child.props as { children?: ReactNode };
      if (props.children !== undefined) {
        const next = interleaveEntityRefs(props.children);
        out.push(cloneElement(child, { key: `entwrap-${counter++}` }, next));
        return;
      }
    }
    out.push(child);
  });
  return out;
}

/** Recursively walk React children, replacing `[N]` substrings inside
 *  string nodes with <CitationChip>. Element nodes are descended into;
 *  non-string non-element nodes pass through unchanged. */
function interleaveCitations(children: ReactNode): ReactNode {
  const out: ReactNode[] = [];
  let counter = 0;
  Children.forEach(children, (child) => {
    if (typeof child === "string") {
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      CITATION_PATTERN.lastIndex = 0;
      while ((match = CITATION_PATTERN.exec(child)) !== null) {
        if (match.index > lastIndex) out.push(child.slice(lastIndex, match.index));
        out.push(<CitationChip key={`cite-${counter++}`} n={match[1]} />);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < child.length) out.push(child.slice(lastIndex));
      return;
    }
    if (isValidElement(child)) {
      const props = child.props as { children?: ReactNode };
      if (props.children !== undefined) {
        const next = interleaveCitations(props.children);
        out.push(cloneElement(child, { key: `wrap-${counter++}` }, next));
        return;
      }
    }
    out.push(child);
  });
  return out;
}

export function Markdown({
  children,
  className = "",
  citations = false,
  entityRefs = true,
}: {
  children: string;
  className?: string;
  citations?: boolean;
  /** Replace [[EntityName]] substrings with clickable EntityRef
   *  chips that open the entity's slide-in panel. Default true so
   *  every markdown surface picks it up; pass `entityRefs={false}`
   *  on surfaces where the syntax should render literally. */
  entityRefs?: boolean;
}) {
  const wrap = (children: ReactNode): ReactNode => {
    let walked: ReactNode = children;
    if (entityRefs) walked = interleaveEntityRefs(walked);
    if (citations) walked = interleaveCitations(walked);
    return walked;
  };
  return (
    <div className={`memso-markdown ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h2 className="text-lg sm:text-base font-bold text-slate-900 dark:text-slate-100 mt-4 first:mt-0 mb-2">
              {wrap(children)}
            </h2>
          ),
          h2: ({ children }) => (
            <h3 className="text-base sm:text-sm font-bold text-slate-900 dark:text-slate-100 mt-4 first:mt-0 mb-1.5">
              {wrap(children)}
            </h3>
          ),
          h3: ({ children }) => (
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-3 first:mt-0 mb-1">
              {wrap(children)}
            </h4>
          ),
          p: ({ children }) => (
            <p className="text-base sm:text-sm leading-relaxed text-slate-700 dark:text-slate-200 mb-2 last:mb-0">
              {wrap(children)}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-5 space-y-1 my-2 text-base sm:text-sm text-slate-700 dark:text-slate-200">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 space-y-1 my-2 text-base sm:text-sm text-slate-700 dark:text-slate-200">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{wrap(children)}</li>,
          code: ({ className, children, ...rest }) => {
            // react-markdown 10 dropped the `inline` prop. Inline code
            // has no language- prefix in className; block code does.
            const isBlock = /\blanguage-/.test(className || "");
            if (isBlock) {
              return (
                <code className={`${className || ""} block text-xs`} {...rest}>
                  {children}
                </code>
              );
            }
            return (
              <code
                className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 text-[0.85em] font-mono text-slate-800 dark:text-slate-100"
                {...rest}
              >
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="rounded-lg bg-slate-100 dark:bg-slate-800 p-3 my-2 overflow-x-auto text-xs font-mono text-slate-800 dark:text-slate-100">
              {children}
            </pre>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 dark:text-orange-400 hover:underline underline-offset-2 break-words"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-slate-900 dark:text-slate-100">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-orange-500 pl-3 italic my-2 text-base sm:text-sm text-slate-600 dark:text-slate-300">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="text-sm border-collapse w-full">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-slate-200 dark:border-slate-700 px-2 py-1 text-left font-semibold bg-slate-50 dark:bg-slate-800/60">
              {wrap(children)}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-slate-200 dark:border-slate-700 px-2 py-1 align-top">{wrap(children)}</td>
          ),
          hr: () => <hr className="my-3 border-slate-200 dark:border-slate-700" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
